import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'
import { useParams, useSearchParams } from 'react-router-dom'
import { RADOM_COLORS } from '../util/Constants'
import styled from 'styled-components'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import Radom, { WithdrawalAccount, WithdrawalRequest } from '../state/Radom'
import { getMethod, ManagedPaymentMethod } from '../util/Managed'
import DestinationDetails from '../components/DestinationDetails'
import WithdrawalDetails from '../components/WithdrawalDetails'
import { errorToast, successToast, trimZeros } from '../util/Util'
import { Chevron } from '../icons/Chevron'
import USDLogo from '../images/USDLogo.svg'
import EUROLogo from '../images/EUROLogo.svg'
import { StatusBadge } from './CheckoutSessions'
import { IconButton } from '../components/Button'
import Copy from '../icons/Copy'
import { SectionContent, SectionItem } from '../components/PaymentDetails'

const SubTitle = styled.div`
  font-size: 14px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
`

const Withdrawal = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const [withdrawalEvent, setWithdrawalEvent] = useState<WithdrawalRequest>()
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [isLoading, setIsLoading] = useState(true)
  const isFiatWithdrawal = useMemo(() => !!withdrawalEvent?.receipt?.FiatReceipt, [withdrawalEvent])
  const [receiptAccount, setReceiptAccount] = useState<WithdrawalAccount>()

  const {
    accountName,
    accountNumber,
    routingNumber,
    accountId
  } = useMemo(() => {
    const accountName = receiptAccount?.name
    const accountNumber =
        receiptAccount?.accountDetails.Fiat?.accountType.us?.accountNumber ||
        receiptAccount?.accountDetails.Fiat?.accountType.iban?.iban
    const routingNumber =
        receiptAccount?.accountDetails.Fiat?.accountType.us?.routingNumber ||
        receiptAccount?.accountDetails.Fiat?.accountType.iban?.bic
    const accountId = receiptAccount?.id
    return {
      accountName,
      accountNumber,
      routingNumber,
      accountId
    }
  }, [receiptAccount])

  const {
    transactionHash,
    networkFeeAmount,
    fiat
  } = useMemo(() => {
    const evm = withdrawalEvent?.receipt?.EVMReceipt
    const dot = withdrawalEvent?.receipt?.DOTReceipt
    const btc = withdrawalEvent?.receipt?.BTCReceipt
    const sol = withdrawalEvent?.receipt?.SOLReceipt
    const fiat = withdrawalEvent?.receipt?.FiatReceipt

    const transactionHash =
    evm?.transactionHash ||
    dot?.transactionHash ||
    btc?.transactionId ||
    sol?.signature ||
    fiat?.transactionHash

    const networkFeeAmount =
    evm?.networkFeeAmount ||
    dot?.networkFeeAmount ||
    btc?.networkFeeAmount ||
    sol?.networkFeeAmount

    const currency: 'EUR' | 'USD' = !fiat?.transferMethod || fiat.transferMethod.toLowerCase() === 'sepa' ? 'EUR' : 'USD'

    return {
      transactionHash,
      networkFeeAmount,
      fiat: {
        transferMethod: fiat?.transferMethod || 'Fiat',
        currencyOut: currency,
        flatFee: fiat?.flatFee,
        percentFee: fiat?.percentFee,
        receiptAccountId: fiat?.withdrawalAccountId,
        finalAmount: fiat?.finalAmount
      }
    }
  }, [withdrawalEvent])

  const logo = useMemo(() => fiat.currencyOut === 'EUR' ? EUROLogo : USDLogo, [fiat])

  const finalAmountCrypto = useMemo(() => {
    if (!withdrawalEvent || !networkFeeAmount) return
    if (!isFiatWithdrawal) {
      return trimZeros((Number(withdrawalEvent.withdrawalAmount) - Number(networkFeeAmount)).toString())
    }
  }, [isFiatWithdrawal, withdrawalEvent, networkFeeAmount])

  useEffect(() => {
    if (!params.id) return
    Radom.getWithdrawal(params.id).then(d => {
      setWithdrawalEvent(d)
      setPaymentMethod(getMethod(d.network, d.token))
    }).finally(() => setIsLoading(false))
  }, [params.id])

  useEffect(() => {
    const accountId = withdrawalEvent?.withdrawalAccountId
    if (accountId) {
      Radom.getWithdrawalAccount(accountId)
        .then(acc => {
          setReceiptAccount(acc)
        })
        .catch(() => setReceiptAccount(undefined))
    } else {
      Radom.listWithdrawalAccounts()
        .then(accs => {
          const actualAccount =
            accs.find(acc => acc.accountDetails.Crypto?.address === withdrawalEvent?.withdrawalAddress)
          setReceiptAccount(actualAccount)
        })
        .catch(() => setReceiptAccount(undefined))
    }
  }, [withdrawalEvent])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (withdrawalEvent) {
    return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <BreadCrumbs items={
          source === 'transactions'
            ? [
              { item: 'Transactions', to: '/transactions' },
              { item: withdrawalEvent.id, to: `/withdrawal/${withdrawalEvent.id}?source=transactions` }
            ]
            : [
              { item: 'Withdrawals', to: '/withdrawals' },
              { item: withdrawalEvent.id, to: '.' }
            ]
        } />
      </div>
      <div style={{
        padding: 30,
        borderRadius: 10,
        border: `1px solid ${RADOM_COLORS.GRAY_DARK}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}>
        <div style={{
          fontSize: '18px',
          paddingBottom: 5,
          display: 'flex',
          flexDirection: 'row',
          gap: 10,
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: '32px'
          }}>

            {paymentMethod &&
              <img src={paymentMethod.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
            }
            <span>{trimZeros(withdrawalEvent.withdrawalAmount)}</span>
            {paymentMethod &&
              <span style={{
                background: RADOM_COLORS.GRAY_MED,
                color: RADOM_COLORS.BLACK,
                borderRadius: 10,
                fontSize: 14,
                padding: '5px 10px',
                fontWeight: 400

              }}>{paymentMethod.ticker}</span>
            }
          </div>
          {isFiatWithdrawal && <>
            <Chevron fill={RADOM_COLORS.GRAY_DARKER} style={{ width: 16, height: 16, transform: 'rotate(-90deg)' }} />
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '32px'
            }}>

              <img src={logo as string} style={{ width: 32, height: 'auto', maxHeight: 32 }} />

              <span>{fiat?.finalAmount}</span>
              <span style={{
                background: RADOM_COLORS.GRAY_MED,
                color: RADOM_COLORS.BLACK,
                borderRadius: 10,
                fontSize: 14,
                padding: '5px 10px',
                fontWeight: 400

              }}>{fiat.currencyOut}</span>

            </div></>}
        </div>

        <SectionContent>
          {withdrawalEvent.withdrawalStatus === 'complete' && <SectionItem>
            <SubTitle>Type</SubTitle>
            <span>{isFiatWithdrawal ? 'Fiat Withdrawal' : 'Crypto Withdrawal'}</span>
          </SectionItem>}
          <SectionItem>
            <SubTitle>Status</SubTitle>
            <StatusBadge status={withdrawalEvent.withdrawalStatus}>{withdrawalEvent.withdrawalStatus}</StatusBadge>
          </SectionItem>
          <SectionItem>
            <SubTitle>Withdrawal request ID</SubTitle>
            <div style={{
              display: 'flex',
              gap: 5
            }}>
              <span>{withdrawalEvent.id}</span>
              <IconButton style={{ padding: 0 }}>
                <Copy style={{ width: 17, stroke: RADOM_COLORS.GRAY_DARKER }} onClick={(e) => {
                  e.stopPropagation()
                  navigator.clipboard
                    .writeText(withdrawalEvent.id)
                    .then(() => {
                      successToast(
                        'Copied withdrawal event id'
                      )
                    })
                    .catch((err) => {
                      console.error('copy failed', err)
                      errorToast(
                        'Failed to copy withdrawal event id'
                      )
                    })
                }} />
              </IconButton>
            </div>
          </SectionItem>

        </SectionContent>
      </div>
      <DestinationDetails
        isFiatWithdrawal={isFiatWithdrawal}
        addressTo={withdrawalEvent.withdrawalAddress}
        transferMethod={withdrawalEvent.withdrawalStatus === 'complete'
          ? isFiatWithdrawal && fiat ? fiat.transferMethod : 'Crypto'
          : undefined}
        accountName={accountName} routingNumber={routingNumber} accountNumber={accountNumber}
        accountId={accountId}
      />
      {withdrawalEvent.withdrawalStatus === 'complete' && paymentMethod &&
        <WithdrawalDetails paymentMethod={paymentMethod} requestedAt={withdrawalEvent.requestedAt} completedAt={
          withdrawalEvent.completedAt} transactionHash={transactionHash} network={withdrawalEvent.network}
        networkFeeAmount={networkFeeAmount} finalAmountCrypto={finalAmountCrypto}
        amount={withdrawalEvent.withdrawalAmount} isFiatWithdrawal={isFiatWithdrawal} fiat={fiat}
        logo={logo as string}/>
      }
    </Container>
  }

  return <div>
    <span>Withdrawal not found</span>
  </div>
})

export default Withdrawal
